import React, { Fragment, useState } from 'react';

import Link from '@atlaskit/link/link';
import { WidthObserver } from '@atlaskit/width-detector/width-observer';
import { Text } from '@atlaskit/primitives/compiled/text';

import {
	getLogSafeErrorAttributes,
	getReactErrorAttributes,
} from '@confluence/monitoring/entry-points/error-attributes.ts';

import { Attribution } from './Attribution.tsx';
import type { AttributionContextValue } from './AttributionContext.tsx';
import type { ErrorComponentProps } from './GenericErrorBoundary.tsx';

// This code was copied from @confluence/splunk/src/builder.ts to resolve a circular dependency.
// If any changes are made here, they should also be made in that file.
const getUrlForQuery = (query: string): string =>
	`https://splunk.paas-inf.net/en-US/app/search/search?q=search%20${encodeURIComponent(
		query,
	)}&display.page.search.mode=smart`;

export function DevErrorDetail({
	attribution,
	error,
	errorInfo,
}: ErrorComponentProps): React.JSX.Element {
	const reactErrorAttributes = getReactErrorAttributes(error);
	const errorHashCode = reactErrorAttributes ? reactErrorAttributes.errorHashCode : undefined;
	const traceId = getLogSafeErrorAttributes(error).traceId;

	let splunkQueryText: string | undefined;
	let splunkQueryHref: string | undefined;
	if (errorHashCode) {
		splunkQueryText = `ext.event.attributes.errorHashCode="${errorHashCode}"`;
		splunkQueryHref = getUrlForQuery(
			[
				'eventtype="micros_analytics-service"',
				'ext.event.product=confluence',
				splunkQueryText,
			].join('\n'),
		);
	}

	const [width, setWidth] = useState(0);

	return (
		<Fragment>
			<WidthObserver setWidth={setWidth} />
			<Text as="p">
				You can also discuss this in the {getDevSupportChannelLink(attribution)} Slack channel.
			</Text>
			{splunkQueryText ? (
				<Text as="p">
					You can search {/* eslint-disable-next-line @atlaskit/design-system/no-html-anchor */}
					<a href={splunkQueryHref}>
						<Text>Splunk</Text>
					</a>{' '}
					for <b>{splunkQueryText}</b>. g{' '}
				</Text>
			) : null}
			{traceId ? (
				<Text as="p">
					Trace ID: <b>{traceId}</b>
				</Text>
			) : null}
			<Text as="p">
				Error:
				{width ? (
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					<div style={{ overflow: 'scroll', width }}>
						<pre>
							{`${error.message || String(error)}`}
							{errorInfo && `${errorInfo.componentStack}`}
						</pre>
					</div>
				) : null}
			</Text>
		</Fragment>
	);
}

function getDevSupportChannelLink(attribution: AttributionContextValue) {
	let href: string;
	let text: string;
	switch (attribution) {
		case Attribution.PAGE_MODES:
			href = 'https://atlassian.slack.com/messages/CKQ8ZJSUQ';
			text = '#cc-page-modes';
			break;
		case Attribution.PAGE_EXTENSIONS:
			href = 'https://atlassian.slack.com/messages/CKCR3SVLK';
			text = '#cc-page-extensions';
			break;
		case Attribution.COMMENTS:
			href = 'https://atlassian.slack.com/messages/C051ARA9QGH';
			text = '#cc-comments';
			break;
		case Attribution.DISCO:
			href = 'https://atlassian.slack.com/messages/CFGSEJDFT';
			text = '#cc-disco';
			break;
		case Attribution.ECOSYSTEM:
			href = 'https://atlassian.slack.com/archives/CFHD7ERB4';
			text = '#cc-integrations-ecosystem-team';
			break;
		case Attribution.WHITEBOARDS:
			href = 'https://atlassian.slack.com/archives/C02PTHXKDUY';
			text = '#help-confluence-whiteboards';
		case Attribution.ADMIN_EXPERIENCE:
			href = 'https://atlassian.slack.com/archives/C0441RTKC3E';
			text = '#cc-admin-exp';
			break;
		case Attribution.CC_IMPORTER:
			href = 'https://atlassian.enterprise.slack.com/archives/C08LESA39S5';
			text = '#cc-importer';
			break;
		case Attribution.PAGE_EXPERIENCES:
			href = 'https://atlassian.enterprise.slack.com/archives/C07CPP220FM';
			text = '#cc-page-experiences';
		default:
			href = 'https://atlassian.slack.com/messages/CFJ90MFKQ';
			text = '#cc-frontend';
			break;
	}
	return (
		<Link target="_blank" href={href}>
			{text}
		</Link>
	);
}
